import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Tag,
  Switch,
  Descriptions,
  Avatar,
  Divider,
  Card,
  Row,
  Col,
  Badge,
  Select,
  Tabs,
  Drawer,
  Empty,
  Spin,
  Image,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  EyeOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  UserAddOutlined,
  KeyOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  FormOutlined,
  FileTextOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import PagesWrapper from '../../../PagesWrapper';
import usersService from '../../../services/usersService';
import styles from './Users.module.css';

const { Title, Text } = Typography;

/**
 * Role color mapping for tags
 */
const roleColors = {
  admin: 'red',
  'content-creator': 'purple',
  'master-trainer': 'blue',
  trainer: 'cyan',
  trainee: 'green',
};

/**
 * Available roles for user creation (including admin)
 */
const availableRoles = [
  { value: 'admin', label: 'Admin' },
  { value: 'content-creator', label: 'Content Creator' },
  { value: 'master-trainer', label: 'Master Trainer' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'trainee', label: 'Trainee' },
];

/**
 * Available roles for role change (excludes admin)
 */
const nonAdminRoles = availableRoles.filter(role => role.value !== 'admin');

/**
 * Available languages
 */
const availableLanguages = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Français' },
];

/**
 * Users Management Page
 * Admin page for managing users: view, disable/enable, change passwords
 */
const Users = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [includeDisabled, setIncludeDisabled] = useState(true);
  
  // View modal state
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Change password modal state
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordForm] = Form.useForm();
  
  // Create user modal state
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();
  
  // Role change state
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('all');

  // Pending users state
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingPagination, setPendingPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [pendingSearchText, setPendingSearchText] = useState('');
  
  // Survey responses drawer state
  const [surveyDrawerVisible, setSurveyDrawerVisible] = useState(false);
  const [surveyResponses, setSurveyResponses] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyUser, setSurveyUser] = useState(null);
  
  // Approval/rejection state
  const [approvalLoading, setApprovalLoading] = useState({});
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectUser, setRejectUser] = useState(null);
  const [rejectForm] = Form.useForm();

  /**
   * Fetches users from the API
   */
  const fetchUsers = useCallback(async (page = 1, pageSize = 10, search = '', includeDisabledParam = true) => {
    setLoading(true);
    try {
      const response = await usersService.getUsers({
        page,
        pageSize,
        search: search || undefined,
        includeDisabled: includeDisabledParam,
      });
      setUsers(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.users.fetchError', 'Failed to load users'));
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Fetches pending users from the API
   */
  const fetchPendingUsers = useCallback(async (page = 1, pageSize = 10, search = '') => {
    setPendingLoading(true);
    try {
      const response = await usersService.getPendingUsers({
        page,
        pageSize,
        search: search || undefined,
      });
      setPendingUsers(response.items || []);
      setPendingPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.users.fetchPendingError', 'Failed to load pending users'));
      console.error('Failed to fetch pending users:', error);
    } finally {
      setPendingLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize, searchText, includeDisabled);
    fetchPendingUsers(pendingPagination.current, pendingPagination.pageSize, pendingSearchText);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles table pagination changes
   */
  const handleTableChange = (newPagination) => {
    fetchUsers(newPagination.current, newPagination.pageSize, searchText, includeDisabled);
  };

  /**
   * Handles search input
   */
  const handleSearch = (value) => {
    setSearchText(value);
    fetchUsers(1, pagination.pageSize, value, includeDisabled);
  };

  /**
   * Handles toggle for including disabled users
   */
  const handleIncludeDisabledChange = (checked) => {
    setIncludeDisabled(checked);
    fetchUsers(1, pagination.pageSize, searchText, checked);
  };

  /**
   * Opens the view details modal
   */
  const openViewModal = (user) => {
    setSelectedUser(user);
    setViewModalVisible(true);
  };

  /**
   * Closes the view details modal
   */
  const closeViewModal = () => {
    setViewModalVisible(false);
    setSelectedUser(null);
  };

  /**
   * Opens the change password modal
   */
  const openPasswordModal = (user) => {
    setPasswordUser(user);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  /**
   * Closes the change password modal
   */
  const closePasswordModal = () => {
    setPasswordModalVisible(false);
    setPasswordUser(null);
    passwordForm.resetFields();
  };

  /**
   * Handles changing a user's password
   */
  const handleChangePassword = async (values) => {
    try {
      await usersService.changeUserPassword(
        passwordUser.id,
        values.newPassword,
        values.confirmPassword
      );
      message.success(t('admin.users.passwordChangeSuccess', 'Password changed successfully'));
      closePasswordModal();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.passwordChangeError', 'Failed to change password'));
    }
  };

  /**
   * Handles enabling/disabling a user
   */
  const handleToggleUserStatus = async (user, disable) => {
    try {
      await usersService.setUserStatus(user.id, disable);
      message.success(
        disable
          ? t('admin.users.disableSuccess', 'User disabled successfully')
          : t('admin.users.enableSuccess', 'User enabled successfully')
      );
      fetchUsers(pagination.current, pagination.pageSize, searchText, includeDisabled);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.statusChangeError', 'Failed to change user status'));
    }
  };

  /**
   * Handles requiring a user to change password on next login
   */
  const handleRequirePasswordChange = async (user) => {
    try {
      await usersService.requirePasswordChange(user.id);
      fetchUsers(pagination.current, pagination.pageSize, searchText, includeDisabled);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.requirePasswordChangeError', 'Failed to require password change'));
    }
  };

  /**
   * Handles revoking all sessions for a user
   */
  const handleRevokeUserSessions = async (user) => {
    try {
      const result = await usersService.revokeUserSessions(user.id);
      if (result.revokedCount === 0) {
        message.info(t('admin.users.noSessionsToRevoke', 'No active sessions to revoke'));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.revokeSessionsError', 'Failed to revoke sessions'));
    }
  };

  /**
   * Handles revoking all trusted devices for a user
   */
  const handleRevokeUserTrustedDevices = async (user) => {
    try {
      const result = await usersService.revokeUserTrustedDevices(user.id);
      if (result.revokedCount === 0) {
        message.info(t('admin.users.noTrustedDevicesToRevoke', 'No trusted devices to revoke'));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.revokeTrustedDevicesError', 'Failed to revoke trusted devices'));
    }
  };

  /**
   * Opens the create user modal
   */
  const openCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      preferredLanguage: 'en',
      role: 'trainee',
    });
    setCreateModalVisible(true);
  };

  /**
   * Closes the create user modal
   */
  const closeCreateModal = () => {
    setCreateModalVisible(false);
    createForm.resetFields();
  };

  /**
   * Handles creating a new user
   */
  const handleCreateUser = async (values) => {
    setCreateLoading(true);
    try {
      await usersService.createUser({
        email: values.email,
        password: values.password,
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        preferredLanguage: values.preferredLanguage,
        role: values.role,
      });
      message.success(t('admin.users.createSuccess', 'User created successfully'));
      closeCreateModal();
      fetchUsers(1, pagination.pageSize, searchText, includeDisabled);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.join(', ') || error.message;
      message.error(errorMessage || t('admin.users.createError', 'Failed to create user'));
    } finally {
      setCreateLoading(false);
    }
  };

  /**
   * Handles changing a user's role
   */
  const handleChangeRole = async (newRole) => {
    if (!selectedUser || !newRole) return;
    
    setRoleChangeLoading(true);
    try {
      const updatedUser = await usersService.changeUserRole(selectedUser.id, newRole);
      // Update selected user with new data
      setSelectedUser(updatedUser);
      // Refresh the user list to show updated role
      fetchUsers(pagination.current, pagination.pageSize, searchText, includeDisabled);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.roleChangeError', 'Failed to change user role'));
    } finally {
      setRoleChangeLoading(false);
    }
  };

  // ===================
  // Pending Users Handlers
  // ===================

  /**
   * Handles pending users table pagination changes
   */
  const handlePendingTableChange = (newPagination) => {
    fetchPendingUsers(newPagination.current, newPagination.pageSize, pendingSearchText);
  };

  /**
   * Handles pending users search
   */
  const handlePendingSearch = (value) => {
    setPendingSearchText(value);
    fetchPendingUsers(1, pendingPagination.pageSize, value);
  };

  /**
   * Approves a pending user
   */
  const handleApproveUser = async (user) => {
    setApprovalLoading(prev => ({ ...prev, [user.id]: 'approve' }));
    try {
      await usersService.approveUser(user.id);
      message.success(t('admin.users.approveSuccess', 'User approved successfully'));
      fetchPendingUsers(pendingPagination.current, pendingPagination.pageSize, pendingSearchText);
      // Also refresh main users list
      fetchUsers(pagination.current, pagination.pageSize, searchText, includeDisabled);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.approveError', 'Failed to approve user'));
    } finally {
      setApprovalLoading(prev => ({ ...prev, [user.id]: null }));
    }
  };

  /**
   * Opens the reject modal
   */
  const openRejectModal = (user) => {
    setRejectUser(user);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  /**
   * Closes the reject modal
   */
  const closeRejectModal = () => {
    setRejectModalVisible(false);
    setRejectUser(null);
    rejectForm.resetFields();
  };

  /**
   * Rejects a pending user
   */
  const handleRejectUser = async (values) => {
    if (!rejectUser) return;
    
    setApprovalLoading(prev => ({ ...prev, [rejectUser.id]: 'reject' }));
    try {
      await usersService.rejectUser(rejectUser.id, { reason: values.reason || undefined });
      message.success(t('admin.users.rejectSuccess', 'User registration rejected'));
      closeRejectModal();
      fetchPendingUsers(pendingPagination.current, pendingPagination.pageSize, pendingSearchText);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.users.rejectError', 'Failed to reject user'));
    } finally {
      setApprovalLoading(prev => ({ ...prev, [rejectUser.id]: null }));
    }
  };

  /**
   * Opens the survey responses drawer
   */
  const openSurveyDrawer = async (user) => {
    setSurveyUser(user);
    setSurveyDrawerVisible(true);
    setSurveyLoading(true);
    setSurveyResponses(null);

    try {
      const responses = await usersService.getUserRegistrationSurveyResponses(user.id);
      setSurveyResponses(responses);
    } catch (error) {
      console.error('Failed to load survey responses:', error);
      message.error(t('admin.users.surveyLoadError', 'Failed to load survey responses'));
    } finally {
      setSurveyLoading(false);
    }
  };

  /**
   * Closes the survey responses drawer
   */
  const closeSurveyDrawer = () => {
    setSurveyDrawerVisible(false);
    setSurveyUser(null);
    setSurveyResponses(null);
  };

  /**
   * Formats a date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  /**
   * Formats a date for short display (date only)
   */
  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  /**
   * Check if user is current logged-in user
   */
  const isCurrentUser = (user) => currentUser?.id === user.id;

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: t('admin.users.columns.user', 'User'),
      key: 'user',
      ellipsis: true,
      render: (_, record) => {
        const name = [record.firstName, record.lastName].filter(Boolean).join(' ');
        const isSelf = isCurrentUser(record);
        
        return (
          <div className={styles.userCell}>
            <Badge 
              dot={isSelf} 
              color="blue" 
              offset={[-2, 2]}
              title={isSelf ? t('admin.users.you', 'You') : undefined}
            >
              <Avatar 
                size={40} 
                icon={<UserOutlined />}
                src={record.profileImageUrl}
                className={record.isDisabled ? styles.avatarDisabled : ''}
              />
            </Badge>
            <div className={styles.userInfo}>
              <div className={styles.userName}>
                {name || <Text type="secondary">{t('admin.users.noName', 'No name')}</Text>}
                {isSelf && (
                  <Tag color="blue" className={styles.youTag}>
                    {t('admin.users.you', 'You')}
                  </Tag>
                )}
              </div>
              <Text type="secondary" className={styles.userEmail}>
                {record.email}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: t('admin.users.columns.status', 'Status'),
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => (
        record.isDisabled ? (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            {t('admin.users.statusDisabled', 'Disabled')}
          </Tag>
        ) : (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t('admin.users.statusActive', 'Active')}
          </Tag>
        )
      ),
    },
    {
      title: t('admin.users.columns.roles', 'Roles'),
      dataIndex: 'roles',
      key: 'roles',
      width: 180,
      render: (roles) => (
        <Space wrap size={[4, 4]}>
          {roles?.map(role => (
            <Tag key={role} color={roleColors[role] || 'default'}>
              {role}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t('admin.users.columns.survey', 'Survey'),
      key: 'survey',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (!record.hasCompletedSurvey) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tooltip title={record.surveyName || t('admin.users.viewSurvey', 'View Survey')}>
            <Button
              type="link"
              size="small"
              icon={<FormOutlined />}
              onClick={() => openSurveyDrawer(record)}
            >
              {t('admin.users.completed', 'Completed')}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: t('admin.users.columns.createdAt', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (date) => formatShortDate(date),
    },
    {
      title: t('admin.users.columns.lastLogin', 'Last Login'),
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 110,
      render: (date) => formatShortDate(date),
    },
    {
      title: t('admin.users.columns.actions', 'Actions'),
      key: 'actions',
      width: 250,
      align: 'center',
      render: (_, record) => {
        const isAdmin = record.roles?.includes('admin');
        const isSelf = isCurrentUser(record);
        
        return (
          <Space size="small" wrap>
            <Tooltip title={t('admin.users.viewDetails', 'View Details')}>
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => openViewModal(record)}
              />
            </Tooltip>
            {!isAdmin && !isSelf && (
              <>
                <Tooltip title={t('admin.users.changePassword', 'Change Password')}>
                  <Button
                    type="text"
                    icon={<LockOutlined />}
                    onClick={() => openPasswordModal(record)}
                  />
                </Tooltip>
                {!record.isDisabled && (
                  <Popconfirm
                    title={t('admin.users.requirePasswordChangeTitle', 'Require Password Change?')}
                    description={t('admin.users.requirePasswordChangeDesc', 'User will be forced to change password on next login. All sessions will be revoked.')}
                    onConfirm={() => handleRequirePasswordChange(record)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                    icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                  >
                    <Tooltip title={t('admin.users.requirePasswordChange', 'Require Password Change')}>
                      <Button
                        type="text"
                        icon={<KeyOutlined />}
                        style={{ color: '#faad14' }}
                      />
                    </Tooltip>
                  </Popconfirm>
                )}
                <Popconfirm
                  title={t('admin.users.revokeSessionsTitle', 'Revoke All Sessions?')}
                  description={t('admin.users.revokeSessionsDesc', 'This will log out the user from all devices immediately.')}
                  onConfirm={() => handleRevokeUserSessions(record)}
                  okText={t('common.yes', 'Yes')}
                  cancelText={t('common.no', 'No')}
                  icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                >
                  <Tooltip title={t('admin.users.revokeSessions', 'Revoke Sessions')}>
                    <Button
                      type="text"
                      icon={<LogoutOutlined />}
                      style={{ color: '#ff4d4f' }}
                    />
                  </Tooltip>
                </Popconfirm>
                <Popconfirm
                  title={t('admin.users.revokeTrustedDevicesTitle', 'Revoke Trusted Devices?')}
                  description={t('admin.users.revokeTrustedDevicesDesc', 'User will need to verify 2FA on next login from all devices.')}
                  onConfirm={() => handleRevokeUserTrustedDevices(record)}
                  okText={t('common.yes', 'Yes')}
                  cancelText={t('common.no', 'No')}
                  icon={<ExclamationCircleOutlined style={{ color: '#722ed1' }} />}
                >
                  <Tooltip title={t('admin.users.revokeTrustedDevices', 'Revoke Trusted Devices')}>
                    <Button
                      type="text"
                      icon={<SafetyCertificateOutlined />}
                      style={{ color: '#722ed1' }}
                    />
                  </Tooltip>
                </Popconfirm>
                {record.isDisabled ? (
                  <Popconfirm
                    title={t('admin.users.enableConfirmTitle', 'Enable User?')}
                    description={t('admin.users.enableConfirmDesc', 'This will allow the user to login again.')}
                    onConfirm={() => handleToggleUserStatus(record, false)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Tooltip title={t('admin.users.enable', 'Enable User')}>
                      <Button
                        type="text"
                        icon={<CheckCircleOutlined />}
                        style={{ color: '#52c41a' }}
                      />
                    </Tooltip>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title={t('admin.users.disableConfirmTitle', 'Disable User?')}
                    description={t('admin.users.disableConfirmDesc', 'This will prevent the user from logging in.')}
                    onConfirm={() => handleToggleUserStatus(record, true)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                    icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                  >
                    <Tooltip title={t('admin.users.disable', 'Disable User')}>
                      <Button
                        type="text"
                        danger
                        icon={<CloseCircleOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  /**
   * Pending users table columns configuration
   */
  const pendingColumns = [
    {
      title: t('admin.users.columns.user', 'User'),
      key: 'user',
      width: 700,
      ellipsis: true,
      render: (_, record) => {
        const name = [record.firstName, record.lastName].filter(Boolean).join(' ');
        
        return (
          <div className={styles.userCell}>
            <Avatar 
              size={40} 
              icon={<UserOutlined />}
              src={record.profileImageUrl}
            />
            <div className={styles.userInfo}>
              <div className={styles.userName}>
                {name || <Text type="secondary">{t('admin.users.noName', 'No name')}</Text>}
              </div>
              <Text type="secondary" className={styles.userEmail}>
                {record.email}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: t('admin.users.columns.emailConfirmed', 'Email'),
      key: 'emailConfirmed',
      width: 120,
      align: 'center',
      render: (_, record) => (
        record.emailConfirmed ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t('admin.users.emailConfirmed', 'Confirmed')}
          </Tag>
        ) : (
          <Tag color="warning" icon={<ClockCircleOutlined />}>
            {t('admin.users.emailPending', 'Pending')}
          </Tag>
        )
      ),
    },
    {
      title: t('admin.users.columns.survey', 'Survey'),
      key: 'survey',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (!record.hasCompletedSurvey) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tooltip title={record.surveyName || t('admin.users.viewSurvey', 'View Survey')}>
            <Button
              type="link"
              size="small"
              icon={<FormOutlined />}
              onClick={() => openSurveyDrawer(record)}
            >
              {t('admin.users.completed', 'Completed')}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: t('admin.users.columns.registeredAt', 'Registered'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (date) => formatShortDate(date),
    },
    {
      title: t('admin.users.columns.termsAccepted', 'Terms'),
      key: 'termsAccepted',
      width: 100,
      align: 'center',
      render: (_, record) => (
        record.termsAcceptedAt ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t('admin.users.termsAccepted', 'Accepted')}
          </Tag>
        ) : (
          <Tag color="default">-</Tag>
        )
      ),
    },
    {
      title: t('admin.users.columns.actions', 'Actions'),
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record) => {
        const loading = approvalLoading[record.id];
        
        return (
          <Space size="small">
            <Popconfirm
              title={t('admin.users.approveConfirmTitle', 'Approve User?')}
              description={t('admin.users.approveConfirmDesc', 'User will receive an email to set their password.')}
              onConfirm={() => handleApproveUser(record)}
              okText={t('common.yes', 'Yes')}
              cancelText={t('common.no', 'No')}
            >
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                loading={loading === 'approve'}
                disabled={!record.emailConfirmed}
              >
                {t('admin.users.approve', 'Approve')}
              </Button>
            </Popconfirm>
            <Button
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              loading={loading === 'reject'}
              onClick={() => openRejectModal(record)}
            >
              {t('admin.users.reject', 'Reject')}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <UserOutlined className={styles.titleIcon} />
            <Title level={2} className={styles.title}>
              {t('admin.users.title', 'Users')}
            </Title>
          </div>
          <Text type="secondary">
            {t('admin.users.description', 'Manage user accounts, permissions, and access')}
          </Text>
        </div>

        {/* Tabs for All Users / Pending Approval */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className={styles.userTabs}
          items={[
            {
              key: 'all',
              label: (
                <span>
                  <UserOutlined />
                  {t('admin.users.tabs.allUsers', 'All Users')}
                </span>
              ),
              children: (
                <>
                  {/* Toolbar Card */}
                  <Card className={styles.toolbarCard} size="small">
                    <Row gutter={[16, 12]} align="middle">
                      <Col xs={24} sm={24} md={10} lg={8}>
                        <Input.Search
                          placeholder={t('admin.users.searchPlaceholder', 'Search by email or name...')}
                          allowClear
                          onSearch={handleSearch}
                          prefix={<SearchOutlined />}
                          className={styles.searchInput}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={8}>
                        <div className={styles.filterGroup}>
                          <FilterOutlined className={styles.filterIcon} />
                          <Text className={styles.filterLabel}>{t('admin.users.showDisabled', 'Show disabled:')}</Text>
                          <Switch 
                            checked={includeDisabled} 
                            onChange={handleIncludeDisabledChange}
                            size="small"
                          />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} md={6} lg={8} className={styles.toolbarActions}>
                        <Space>
                          <Text type="secondary" className={styles.totalCount}>
                            {t('admin.users.totalUsers', '{{count}} users', { count: pagination.total })}
                          </Text>
                          <Tooltip title={t('common.refresh', 'Refresh')}>
                            <Button
                              icon={<ReloadOutlined />}
                              onClick={() => fetchUsers(pagination.current, pagination.pageSize, searchText, includeDisabled)}
                            />
                          </Tooltip>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreateModal}
                          >
                            {t('admin.users.createUser', 'Create user')}
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </Card>

                  <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      showTotal: (total, range) =>
                        t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                          start: range[0],
                          end: range[1],
                          total,
                        }),
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 800 }}
                    rowClassName={(record) => isCurrentUser(record) ? styles.currentUserRow : ''}
                  />
                </>
              ),
            },
            {
              key: 'pending',
              label: (
                <Badge count={pendingPagination.total} offset={[10, 0]} size="small">
                  <span>
                    <ClockCircleOutlined />
                    {t('admin.users.tabs.pendingApproval', 'Pending Approval')}
                  </span>
                </Badge>
              ),
              children: (
                <>
                  {/* Pending Users Toolbar */}
                  <Card className={styles.toolbarCard} size="small">
                    <Row gutter={[16, 12]} align="middle">
                      <Col xs={24} sm={24} md={12} lg={10}>
                        <Input.Search
                          placeholder={t('admin.users.searchPlaceholder', 'Search by email or name...')}
                          allowClear
                          onSearch={handlePendingSearch}
                          prefix={<SearchOutlined />}
                          className={styles.searchInput}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={14} className={styles.toolbarActions}>
                        <Space>
                          <Text type="secondary" className={styles.totalCount}>
                            {t('admin.users.pendingCount', '{{count}} pending', { count: pendingPagination.total })}
                          </Text>
                          <Tooltip title={t('common.refresh', 'Refresh')}>
                            <Button
                              icon={<ReloadOutlined />}
                              onClick={() => fetchPendingUsers(pendingPagination.current, pendingPagination.pageSize, pendingSearchText)}
                            />
                          </Tooltip>
                        </Space>
                      </Col>
                    </Row>
                  </Card>

                  <Table
                    columns={pendingColumns}
                    dataSource={pendingUsers}
                    rowKey="id"
                    loading={pendingLoading}
                    pagination={{
                      ...pendingPagination,
                      showSizeChanger: true,
                      showTotal: (total, range) =>
                        t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                          start: range[0],
                          end: range[1],
                          total,
                        }),
                    }}
                    onChange={handlePendingTableChange}
                    scroll={{ x: 800 }}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={t('admin.users.noPendingUsers', 'No pending registrations')}
                        />
                      ),
                    }}
                  />
                </>
              ),
            },
          ]}
        />

        {/* View User Details Modal */}
        <Modal
          title={
            <Space>
              {t('admin.users.viewTitle', 'User Details')}
              {selectedUser && isCurrentUser(selectedUser) && (
                <Tag color="blue">{t('admin.users.you', 'You')}</Tag>
              )}
            </Space>
          }
          open={viewModalVisible}
          onCancel={closeViewModal}
          footer={[
            <Button key="close" onClick={closeViewModal}>
              {t('common.close', 'Close')}
            </Button>
          ]}
          width={600}
        >
          {selectedUser && (
            <div className={styles.userDetails}>
              <div className={styles.userHeader}>
                <Avatar 
                  size={80} 
                  icon={<UserOutlined />}
                  src={selectedUser.profileImageUrl}
                  className={selectedUser.isDisabled ? styles.avatarDisabled : ''}
                />
                <div className={styles.userHeaderInfo}>
                  <Title level={4} style={{ margin: 0 }}>
                    {[selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ') || (
                      <Text type="secondary">{t('admin.users.noName', 'No name')}</Text>
                    )}
                  </Title>
                  <Text type="secondary" className={styles.modalEmail}>{selectedUser.email}</Text>
                  <div className={styles.statusBadge}>
                    {selectedUser.isDisabled ? (
                      <Tag color="error" icon={<CloseCircleOutlined />}>
                        {t('admin.users.statusDisabled', 'Disabled')}
                      </Tag>
                    ) : (
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        {t('admin.users.statusActive', 'Active')}
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
              
              <Divider />
              
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label={t('admin.users.details.id', 'User ID')}>
                  <Text copyable={{ text: selectedUser.id }} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {selectedUser.id}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('admin.users.details.email', 'Email')}>
                  {selectedUser.email}
                </Descriptions.Item>
                <Descriptions.Item label={t('admin.users.details.firstName', 'First Name')}>
                  {selectedUser.firstName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('admin.users.details.lastName', 'Last Name')}>
                  {selectedUser.lastName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('admin.users.details.phone', 'Phone Number')}>
                  {selectedUser.phoneNumber || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('admin.users.details.language', 'Preferred Language')}>
                  <Tag>{selectedUser.preferredLanguage}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('admin.users.details.roles', 'Roles')}>
                  {/* Admin users or self: show static tags */}
                  {(selectedUser.roles?.includes('admin') || isCurrentUser(selectedUser)) ? (
                    <Space wrap>
                      {selectedUser.roles?.map(role => (
                        <Tag key={role} color={roleColors[role] || 'default'}>
                          {role}
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    /* Non-admin users: show role selector */
                    <Space>
                      <Select
                        value={selectedUser.roles?.[0]}
                        onChange={handleChangeRole}
                        options={nonAdminRoles}
                        loading={roleChangeLoading}
                        disabled={roleChangeLoading}
                        style={{ minWidth: 150 }}
                      />
                    </Space>
                  )}
                </Descriptions.Item>
                {selectedUser.isDisabled && (
                  <>
                    <Descriptions.Item label={t('admin.users.details.disabledAt', 'Disabled At')}>
                      {formatDate(selectedUser.disabledAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.details.disabledBy', 'Disabled By')}>
                      {selectedUser.disabledByUserId || '-'}
                    </Descriptions.Item>
                  </>
                )}
                <Descriptions.Item label={t('admin.users.details.createdAt', 'Created At')}>
                  {formatDate(selectedUser.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label={t('admin.users.details.lastLogin', 'Last Login')}>
                  {formatDate(selectedUser.lastLoginAt)}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
        </Modal>

        {/* Create User Modal */}
        <Modal
          title={
            <Space>
              <UserAddOutlined />
              {t('admin.users.createUserTitle', 'Create New User')}
            </Space>
          }
          open={createModalVisible}
          onCancel={closeCreateModal}
          footer={null}
          destroyOnClose
          width={520}
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateUser}
            initialValues={{
              preferredLanguage: 'en',
              role: 'trainee',
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label={t('admin.users.form.firstName', 'First Name')}
                  rules={[
                    { required: true, message: t('admin.users.form.firstNameRequired', 'Please enter first name') },
                  ]}
                >
                  <Input placeholder={t('admin.users.form.firstNamePlaceholder', 'Enter first name')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  label={t('admin.users.form.lastName', 'Last Name')}
                  rules={[
                    { required: true, message: t('admin.users.form.lastNameRequired', 'Please enter last name') },
                  ]}
                >
                  <Input placeholder={t('admin.users.form.lastNamePlaceholder', 'Enter last name')} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label={t('admin.users.form.email', 'Email Address')}
              rules={[
                { required: true, message: t('admin.users.form.emailRequired', 'Please enter email address') },
                { type: 'email', message: t('admin.users.form.emailInvalid', 'Please enter a valid email address') },
              ]}
            >
              <Input placeholder={t('admin.users.form.emailPlaceholder', 'user@example.com')} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="role"
                  label={t('admin.users.form.role', 'Role')}
                  rules={[
                    { required: true, message: t('admin.users.form.roleRequired', 'Please select a role') },
                  ]}
                >
                  <Select
                    placeholder={t('admin.users.form.rolePlaceholder', 'Select role')}
                    options={availableRoles.map(role => ({
                      ...role,
                      label: t(`roles.${role.value}`, role.label),
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="preferredLanguage"
                  label={t('admin.users.form.language', 'Language')}
                >
                  <Select
                    placeholder={t('admin.users.form.languagePlaceholder', 'Select language')}
                    options={availableLanguages}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider className={styles.formDivider}>
              <Text type="secondary">{t('admin.users.form.credentials', 'Credentials')}</Text>
            </Divider>

            <Form.Item
              name="password"
              label={t('admin.users.form.password', 'Password')}
              rules={[
                { required: true, message: t('admin.users.form.passwordRequired', 'Please enter password') },
                { min: 12, message: t('auth.passwordTooShort', 'Password must be at least {{min}} characters', { min: 12 }) },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{12,}$/,
                  message: t('auth.passwordRequirements', 'Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character'),
                },
              ]}
              extra={t('admin.users.form.passwordHint', 'Min 12 characters with uppercase, lowercase, number, and special character')}
            >
              <Input.Password placeholder={t('admin.users.form.passwordPlaceholder', 'Enter password')} />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={t('admin.users.form.confirmPassword', 'Confirm Password')}
              dependencies={['password']}
              rules={[
                { required: true, message: t('auth.confirmPasswordRequired', 'Please confirm your password') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth.passwordsMustMatch', 'Passwords do not match')));
                  },
                }),
              ]}
            >
              <Input.Password placeholder={t('admin.users.form.confirmPasswordPlaceholder', 'Confirm password')} />
            </Form.Item>

            <Form.Item className={styles.formActions}>
              <Space>
                <Button onClick={closeCreateModal}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={createLoading} icon={<UserAddOutlined />}>
                  {t('admin.users.createUser', 'Create User')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Change Password Modal */}
        <Modal
          title={t('admin.users.changePasswordTitle', 'Change User Password')}
          open={passwordModalVisible}
          onCancel={closePasswordModal}
          footer={null}
          destroyOnClose
        >
          {passwordUser && (
            <>
              <div className={styles.passwordModalHeader}>
                <div className={styles.passwordUserInfo}>
                  <Avatar 
                    size={40} 
                    icon={<UserOutlined />}
                    src={passwordUser.profileImageUrl}
                  />
                  <div>
                    <Text strong>{[passwordUser.firstName, passwordUser.lastName].filter(Boolean).join(' ') || passwordUser.email}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>{passwordUser.email}</Text>
                  </div>
                </div>
              </div>
              
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleChangePassword}
              >
                <Form.Item
                  name="newPassword"
                  label={t('auth.newPassword', 'New Password')}
                  rules={[
                    { required: true, message: t('admin.users.form.passwordRequired', 'Please enter new password') },
                    { min: 12, message: t('auth.passwordTooShort', 'Password must be at least {{min}} characters', { min: 12 }) },
                    { 
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{12,}$/,
                      message: t('auth.passwordRequirements', 'Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character')
                    },
                  ]}
                >
                  <Input.Password placeholder={t('auth.newPasswordPlaceholder', 'Enter your new password')} />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label={t('auth.confirmPassword', 'Confirm Password')}
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: t('auth.confirmPasswordRequired', 'Please confirm your password') },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('auth.passwordsMustMatch', 'Passwords do not match')));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder={t('auth.confirmPasswordPlaceholder', 'Confirm your password')} />
                </Form.Item>

                <Form.Item className={styles.formActions}>
                  <Space>
                    <Button onClick={closePasswordModal}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button type="primary" htmlType="submit" icon={<LockOutlined />}>
                      {t('admin.users.changePassword', 'Change Password')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </>
          )}
        </Modal>

        {/* Reject User Modal */}
        <Modal
          title={t('admin.users.rejectUserTitle', 'Reject Registration')}
          open={rejectModalVisible}
          onCancel={closeRejectModal}
          footer={null}
          destroyOnClose
        >
          {rejectUser && (
            <>
              <div className={styles.passwordModalHeader}>
                <div className={styles.passwordUserInfo}>
                  <Avatar 
                    size={40} 
                    icon={<UserOutlined />}
                  />
                  <div>
                    <Text strong>{[rejectUser.firstName, rejectUser.lastName].filter(Boolean).join(' ') || rejectUser.email}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>{rejectUser.email}</Text>
                  </div>
                </div>
              </div>
              
              <Form
                form={rejectForm}
                layout="vertical"
                onFinish={handleRejectUser}
              >
                <Form.Item
                  name="reason"
                  label={t('admin.users.form.rejectReason', 'Reason for rejection (optional)')}
                >
                  <Input.TextArea 
                    rows={3} 
                    placeholder={t('admin.users.form.rejectReasonPlaceholder', 'Enter a reason (will be included in the email)')}
                  />
                </Form.Item>

                <Form.Item className={styles.formActions}>
                  <Space>
                    <Button onClick={closeRejectModal}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button 
                      danger 
                      type="primary" 
                      htmlType="submit" 
                      loading={approvalLoading[rejectUser?.id] === 'reject'}
                      icon={<CloseCircleOutlined />}
                    >
                      {t('admin.users.reject', 'Reject')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </>
          )}
        </Modal>

        {/* Survey Responses Drawer */}
        <Drawer
          title={
            <Space>
              <FormOutlined />
              {t('admin.users.surveyResponsesTitle', 'Survey Responses')}
            </Space>
          }
          placement="right"
          width={680}
          onClose={closeSurveyDrawer}
          open={surveyDrawerVisible}
        >
          {surveyUser && (
            <Card size="small" className={styles.surveyUserCard}>
              <div className={styles.surveyDrawerHeader}>
                <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                <div className={styles.surveyUserInfo}>
                  <Text strong style={{ fontSize: '16px' }}>
                    {[surveyUser.firstName, surveyUser.lastName].filter(Boolean).join(' ') || t('admin.users.noName', 'No name')}
                  </Text>
                  <Text type="secondary">{surveyUser.email}</Text>
                </div>
              </div>
            </Card>
          )}

          {surveyLoading ? (
            <div className={styles.surveyLoading}>
              <Spin size="large" />
              <Text>{t('common.loading', 'Loading...')}</Text>
            </div>
          ) : surveyResponses ? (
            <>
              {/* Survey Metadata Card */}
              <Card 
                size="small" 
                className={styles.surveyMetaCard}
                title={
                  <Space>
                    <FileTextOutlined style={{ color: '#1890ff' }} />
                    <span>{surveyResponses.surveyName || t('admin.users.unnamedSurvey', 'Registration Survey')}</span>
                  </Space>
                }
              >
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <div className={styles.surveyMetaItem}>
                      <CalendarOutlined className={styles.surveyMetaIcon} />
                      <div>
                        <Text type="secondary" className={styles.surveyMetaLabel}>
                          {t('admin.users.surveyStartedAt', 'Started')}
                        </Text>
                        <Text className={styles.surveyMetaValue}>
                          {surveyResponses.startedAt ? formatDate(surveyResponses.startedAt) : '-'}
                        </Text>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className={styles.surveyMetaItem}>
                      <CheckCircleOutlined className={styles.surveyMetaIcon} style={{ color: '#52c41a' }} />
                      <div>
                        <Text type="secondary" className={styles.surveyMetaLabel}>
                          {t('admin.users.surveyCompletedAt', 'Completed')}
                        </Text>
                        <Text className={styles.surveyMetaValue}>
                          {surveyResponses.completedAt ? formatDate(surveyResponses.completedAt) : t('admin.users.surveyNotCompleted', 'Not completed')}
                        </Text>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>

              {/* Answers Section */}
              <div className={styles.surveyAnswersSection}>
               
                {surveyResponses.responses?.length > 0 ? (
                  <div className={styles.surveyResponsesList}>
                    {surveyResponses.responses.map((item, index) => (
                      <Card 
                        key={item.questionId || index} 
                        size="small" 
                        className={styles.surveyResponseCard}
                      >
                        <div className={styles.surveyQuestionHeader}>
                          <span className={styles.surveyQuestionNumber}>{index + 1}</span>
                          <Text strong className={styles.surveyQuestionName}>
                            {item.questionName || t('admin.users.unnamedQuestion', 'Question')}
                          </Text>
                        </div>
                        {item.questionBody && (
                          <Text type="secondary" className={styles.surveyQuestionBody}>
                            {item.questionBody}
                          </Text>
                        )}
                        {item.questionImageUrl && (
                          <div className={styles.surveyQuestionImage}>
                            <Image 
                              src={item.questionImageUrl} 
                              alt="" 
                              style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', marginTop: 8 }}
                            />
                          </div>
                        )}
                        <div className={styles.surveyAnswerBox}>
                          {/* Choice-based answer */}
                          {item.selectedChoiceText && (
                            <div className={styles.surveyChoiceAnswerContainer}>
                              {item.choiceImageUrl && (
                                <Image 
                                  src={item.choiceImageUrl} 
                                  alt="" 
                                  style={{ maxWidth: 80, maxHeight: 80, objectFit: 'contain', marginRight: 12 }}
                                />
                              )}
                              <Text className={styles.surveyChoiceAnswer}>
                                {item.selectedChoiceText}
                              </Text>
                            </div>
                          )}
                          {/* Text answer */}
                          {item.textValue && (
                            <Text className={styles.surveyTextAnswer}>{item.textValue}</Text>
                          )}
                          {/* Numeric answer */}
                          {item.numericValue !== null && item.numericValue !== undefined && (
                            <Text strong>{item.numericValue}</Text>
                          )}
                          {/* No answer */}
                          {!item.selectedChoiceText && !item.textValue && (item.numericValue === null || item.numericValue === undefined) && (
                            <Text type="secondary" italic>
                              {t('admin.users.surveyNoAnswer', 'No answer provided')}
                            </Text>
                          )}
                        </div>
                       
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Empty 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('admin.users.surveyNoResponses', 'No responses recorded')} 
                  />
                )}
              </div>
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('admin.users.surveyLoadError', 'Failed to load survey responses')} 
            />
          )}
        </Drawer>
      </div>
    </PagesWrapper>
  );
};

export default Users;
